// adapted from https://github.com/brophdawg11/react-router-auth0-example/blob/91ad7ba916d8a3ecc348c037e1e534b4d87360cd/src/auth.ts
// and https://gist.github.com/klequis/fd9c3cf5b025cc26a282ea30ef8ff6da

import { getChatAPI } from '@/api/ChatApi';

import {
  Auth0Client as Auth0ClientClass,
  createAuth0Client,
  User,
} from '@auth0/auth0-spa-js';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

class Auth0Client {
  #auth0Client: Auth0ClientClass | undefined;

  #getClient = async () => {
    const AUTH0_DOMAIN = process.env.AUTH0_DOMAIN;
    const AUTH0_CLIENT_ID = process.env.AUTH0_CLIENT_ID;
    const AUTH0_API_AUDIENCE = process.env.AUTH0_API_AUDIENCE;

    if (this.#auth0Client == null) {
      if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) {
        throw new Error('Auth0 env variables are missing');
      }

      this.#auth0Client = await createAuth0Client({
        domain: AUTH0_DOMAIN,
        clientId: AUTH0_CLIENT_ID,
        authorizationParams: {
          // This isn't noted in the docs but it's needed if you want to use the token on the API end
          audience: AUTH0_API_AUDIENCE,
        },
        // if we set up a custom auth0 domain we can get rid of useRefreshTokens and cacheLocation
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
      });
    }

    return this.#auth0Client;
  };

  getToken = async (): Promise<string | undefined> => {
    const client = await this.#getClient();

    if (await client.isAuthenticated()) {
      try {
        return await client.getTokenSilently();
      } catch (e) {
        if (e instanceof Error) {
          console.error(
            `Something went wrong when getting the token: ${e.message}\nLogging in again.`,
          );
        }

        // force logout in case there are expired tokens in localstorage, then relogin
        await this.logout();
        await this.login();
        throw e;
      }
    }

    return undefined;
  };

  isAuthenticated = async (): Promise<boolean> => {
    const client = await this.#getClient();

    return client.isAuthenticated();
  };

  getUserInfo = async (): Promise<User | undefined> => {
    const client = await this.#getClient();

    return await client.getUser();
  };

  login = async (): Promise<void> => {
    const client = await this.#getClient();
    const urlParams = new URLSearchParams(window.location.search);

    client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/chat?${urlParams.toString()}`,
        prompt: 'login', // Forces fresh login so user can choose which Google account to use and won't get stuck if they previously declined permissions during the flow
      },
    });
  };

  handleLoginRedirect = async (): Promise<void> => {
    const query = window.location.search;
    if (query.includes('code=') && query.includes('state=')) {
      const client = await this.#getClient();
      await client.handleRedirectCallback();
    }
  };

  logout = async (): Promise<void> => {
    const client = await this.#getClient();

    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };
}

export const auth0Client = new Auth0Client();

interface Auth0ContextType {
  isAuthenticated: boolean;
  auth0User: User | undefined;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const defaultAuth0Context = {
  isAuthenticated: false,
  auth0User: undefined,
  login: async () => {},
  logout: async () => {},
};

export const Auth0Context =
  createContext<Auth0ContextType>(defaultAuth0Context);
export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({ children }: { children: ReactNode }) => {
  const chatApi = getChatAPI();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [auth0User, setAuth0User] = useState<User | undefined>(undefined);

  useEffect(() => {
    const checkAuth = async () => {
      if (window.location.search.includes('code=')) {
        await auth0Client.handleLoginRedirect();
      }
      const isAuthenticated = await auth0Client.isAuthenticated();
      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0Client.getUserInfo();
        setAuth0User(user);
      }
    };

    checkAuth();
  }, []);

  const login = async () => {
    await auth0Client.login();
    const user = await auth0Client.getUserInfo();

    setIsAuthenticated(true);
    setAuth0User(user);
  };

  const logout = async () => {
    await auth0Client.logout();
    chatApi.resetAnonymousUserId();
    setIsAuthenticated(false);
    setAuth0User(undefined);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        auth0User,
        login,
        logout,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
